import React, { useState } from 'react';
import { AvCheckbox } from '@fabric-ui/components';
import { Row, Table as TanstackTable } from '@tanstack/react-table';
import { useAvTableV2Context } from './AvTableV2Provider';
import { DataRecord } from './table.types';

interface ISelectAllHeaderProps {
  table: TanstackTable<DataRecord>;
  handleToggleAllPageRows: (table: TanstackTable<DataRecord>, e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const SelectAllHeader = ({ table, handleToggleAllPageRows }: ISelectAllHeaderProps) => (
  <AvCheckbox
    disabled={!table.options.enableRowSelection}
    value={table.getIsAllPageRowsSelected()}
    onChange={(_, e) => {
      handleToggleAllPageRows(table, e);
    }}
    indeterminate={table.getIsSomeRowsSelected()}
    inputProps={{ 'aria-label': 'select all rows' }}
  />
);

interface SelectCellProps {
  row: Row<DataRecord>;
  table: TanstackTable<DataRecord>;
}

export const SelectCell = ({ row, table }: SelectCellProps) => {
  useAvTableV2Context();
  return (
    <AvCheckbox
      disabled={!row.getCanSelect() || !table.options.enableRowSelection}
      value={row.getIsSelected()}
      onChange={(_, e) => {
        row.getToggleSelectedHandler()(e);
      }}
    />
  );
};

interface IUseRowSelectionProps {
  onIsAllRowsSelectedChange?: (isAllRowsSelected: boolean) => void;
}

export const useRowSelection = ({ onIsAllRowsSelectedChange }: IUseRowSelectionProps) => {
  const [isClickedAll, setIsClickedAll] = useState(false);
  const [showSelectAllRowsButton, setShowSelectedAllRowsButton] = useState(false);

  const handleToggleAllPageRows = (table: TanstackTable<DataRecord>, e: React.ChangeEvent<HTMLInputElement>) => {
    if (isClickedAll) {
      setIsClickedAll(false);
      table.toggleAllRowsSelected(false);
      table.toggleAllPageRowsSelected(true);
      onIsAllRowsSelectedChange?.(false);
    } else {
      table.getToggleAllPageRowsSelectedHandler()(e);
      onIsAllRowsSelectedChange?.(false);
    }
    if (e.target.checked) {
      const totalRows = table.getRowCount();
      const pageRows = table.getRowModel().rows.length;
      if (!table.getIsAllPageRowsSelected() && pageRows < totalRows && !!onIsAllRowsSelectedChange) {
        setShowSelectedAllRowsButton(true);
      }
    } else {
      setShowSelectedAllRowsButton(false);
    }
  };

  const handleSelectAllRowsClick = (table: TanstackTable<DataRecord>) => {
    table.toggleAllRowsSelected(true);
    setIsClickedAll(true);
    onIsAllRowsSelectedChange?.(true);
  };

  const renderSelectAllHeader = ({ table }: { table: TanstackTable<DataRecord> }) => (
    <SelectAllHeader table={table} handleToggleAllPageRows={handleToggleAllPageRows} />
  );

  const renderSelectCell = ({ row, table }: { row: Row<DataRecord>; table: TanstackTable<DataRecord> }) => (
    <SelectCell row={row} table={table} />
  );

  return {
    isClickedAll,
    setIsClickedAll,
    showSelectAllRowsButton,
    setShowSelectedAllRowsButton,
    renderSelectAllHeader,
    renderSelectCell,
    handleSelectAllRowsClick,
  };
};
